/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { Container, useToast } from '@crisellevaldez/speculo-ui';
import { HelpCircle } from 'lucide-react';
import { ScheduleCallModal } from '../components/features/header/ScheduleCallModal';
import { OnboardingProgress } from '../components/features/onboarding/components/OnboardingProgress';
import { StagePreWelcome } from '../components/features/onboarding/stages/StagePreWelcome';
import { Stage0Welcome } from '../components/features/onboarding/stages/Stage0Welcome';
import { Stage1OrganizationSetup } from '../components/features/onboarding/stages/Stage1OrganizationSetup';
import { Stage2WebsiteBranding } from '../components/features/onboarding/stages/Stage2WebsiteBranding';
import { Stage3RemiSetup } from '../components/features/onboarding/stages/Stage3RemiSetup';
import { Stage4IDXSetupWrapper } from '../components/features/onboarding/stages/Stage4IDXSetupWrapper';
import { Stage5CRMSetup } from '../components/features/onboarding/stages/Stage5CRMSetup';
import { LastStageGuide } from '../components/features/onboarding/stages/LastStageGuide';
import { TwilioStatusBanner } from '../components/features/onboarding/components/TwilioStatusBanner';
import { MLSStatusBanner } from '../components/features/onboarding/components/MLSStatusBanner';
import { WebsiteStatusBanner } from '../components/features/onboarding/components/WebsiteStatusBanner';
import { IntegrationsStatusBanner } from '../components/features/onboarding/components/IntegrationsStatusBanner';
import { useOnboardingData } from '../components/features/onboarding/hooks/useOnboardingData';
import { useOnboardingSecrets } from '../components/features/onboarding/hooks/useOnboardingSecrets';
import {
  OrganizationSetupData,
  WebsiteBrandingData,
  RemiSetupData,
  TwilioSetupData,
  IDXSetupData,
  CRMSetupData,
} from '../components/features/onboarding/schemas/onboarding.schema';
import { useAuthStore } from '../contexts/auth/useAuthStore';
import { useOrganizationStore } from '../stores/useOrganizationStore';
import { useUpdateOrganizationSettings } from '../hooks/organizations/useUpdateOrganizationSettings';
import { useGeneralKnowledgeBase } from '../hooks/client/useGeneralKnowledgeBase';
import { useQueryClient } from '@tanstack/react-query';
import { clearOrganizationCache } from '../utils/cacheUtils';
import { useOrganizationLogo } from '../hooks/organizations/useOrganizationLogo';
import { useOrganizationFavicon } from '../hooks/organizations/useOrganizationFavicon';
import { useCreateWebsite } from '../hooks/client/tenant/useCreateWebsite';
import { useAutoCreateFlows } from '../components/features/onboarding/hooks/useAutoCreateFlows';

const ONBOARDING_STAGES = [
  'Organization',
  'Website',
  'AI Assistant',
  'IDX',
  'Integrations',
  'Review',
];

export const ClientOnboarding: React.FC = () => {
  const { user } = useAuthStore();
  const { organization, fetchOrganization } = useOrganizationStore();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreWelcome, setShowPreWelcome] = useState(true);
  const [isScheduleCallModalOpen, setIsScheduleCallModalOpen] = useState(false);
  // Tracks whether user clicked "Start Setup" on Stage 0 (before any backend save)
  const [userHasStarted, setUserHasStarted] = useState(false);
  // These hooks are initialized for their side-effects (skipped flows in Stage4IDXSetupWrapper)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { uploadLogo } = useOrganizationLogo();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { uploadFavicon } = useOrganizationFavicon();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const createWebsite = useCreateWebsite();

  // Hook for auto-creating flows after Stage 3
  const { createAllFlows } = useAutoCreateFlows();

  // Hook for saving sensitive onboarding data to Secret Manager (not Firestore)
  const { saveDomainSecrets, saveIDXDomainSecrets, saveBusinessTaxSecrets, saveCRMCredentials } =
    useOnboardingSecrets();

  // Calculate initial stage based on completed stages in Firebase
  const getInitialStage = (): number => {
    if (!organization) return 0;

    const setup = organization.onboardingSetup;

    // If no onboarding data at all, show welcome screen (Stage 0)
    if (!setup || Object.keys(setup).length === 0) {
      return 0;
    }

    // If onboarding data exists, check which stages are completed
    if (setup.stage5?.completed) return 6; // Stage 5 done (CRM), go to stage 6 (Review)
    if (setup.stage4?.completed) return 5; // Stage 4 done (IDX), go to stage 5 (CRM)
    if (setup.stage3?.completed) return 4; // Stage 3 done (includes Twilio), go to stage 4
    if (setup.stage2?.completed) return 3; // Stage 2 done, go to stage 3
    if (setup.stage1?.completed) return 2; // Stage 1 done, go to stage 2

    // Has onboarding data but no stages completed yet → go to Stage 1 (skip welcome)
    return 1;
  };

  const {
    onboardingData,
    saveStage1Data,
    saveStage2Data,
    saveStage3Data,
    saveStage4Data,
  } = useOnboardingData(getInitialStage());

  // firestoreStage drives everything once backend has data (stage > 0).
  // When backend has no data (stage 0), allow userHasStarted to push to stage 1
  // without relying on localStage (which may be stale from cache).
  const firestoreStage = getInitialStage();
  const currentStage = firestoreStage > 0 ? firestoreStage : userHasStarted ? 1 : 0;

  const { updateSettings } = useUpdateOrganizationSettings();

  const { addKnowledgeBase } = useGeneralKnowledgeBase(organization?.id);

  const handleStage1Submit = async (data: OrganizationSetupData) => {
    if (!organization?.id) {
      showToast({
        message: 'No organization found',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Save data locally
      saveStage1Data(data);

      await updateSettings({
        id: organization.id,
        data: {
          org_name: data.org_name,
          org_phone: data.org_phone,
          email: data.email,
          notification_email: data.notification_email,
          client_address: data.client_address,
          timezone: data.timezone,
          onboardingSetup: {
            ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
            stage1: {
              completed: true,
              createdAt: new Date().toISOString(),
            },
          },
        } as any,
      });

      // TODO: Send webhook/Zapier notification
      console.log('Stage 1 saved to Firebase:', data);

      // Clear both React Query and Zustand caches
      await clearOrganizationCache(queryClient, fetchOrganization);

      showToast({
        message: 'Settings saved successfully',
        variant: 'success',
      });

      // Scroll to top (stage is now updated with fresh data)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting stage 1:', error);
      showToast({
        message: 'Failed to save organization settings',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStage2Submit = async (data: WebsiteBrandingData) => {
    if (!organization?.id) {
      showToast({
        message: 'No organization found',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Save data locally
      saveStage2Data(data);

      // Check if skipping website setup
      const isSkipping = data.is_primary_website === 'no';

      // Save domain registrar secrets to Secret Manager (if provided)
      // This saves them securely instead of storing in Firestore
      if (data.registrar_username || data.registrar_password) {
        await saveDomainSecrets(organization.id, {
          domain_registrar: data.domain_registrar,
          registrar_username: data.registrar_username,
          registrar_password: data.registrar_password,
          registrar_email: data.registrar_email,
          registrar_phone: data.registrar_phone,
        });
      }

      // Remove sensitive fields and undefined values from data before saving to Firestore
      const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
        // Skip sensitive fields - they're now stored in Secret Manager
        if (['registrar_username', 'registrar_password'].includes(key)) {
          return acc;
        }
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      // Save website branding data to onboardingData.website (not root website_branding)
      await updateSettings({
        id: organization.id,
        data: {
          onboardingData: {
            ...(useOrganizationStore.getState().organization?.onboardingData ?? {}),
            website: isSkipping
              ? {
                  is_primary_website: data.is_primary_website,
                  skipped: true,
                }
              : cleanData,
          },
          onboardingSetup: {
            ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
            stage2: {
              completed: true,
              createdAt: new Date().toISOString(),
            },
          },
        } as any,
      });

      console.log('Stage 2 saved to Firebase (secrets saved separately):', data);

      // Clear both React Query and Zustand caches
      await clearOrganizationCache(queryClient, fetchOrganization);

      showToast({
        message: 'Settings saved successfully',
        variant: 'success',
      });

      // Scroll to top (stage is now updated with fresh data)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting stage 2:', error);
      showToast({
        message: 'Failed to save website branding',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStage3Submit = async (data: RemiSetupData & { twilioData?: TwilioSetupData }) => {
    if (!organization?.id) {
      showToast({
        message: 'No organization found',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Save data locally
      saveStage3Data(data);

      // Build knowledge base array from form data
      const knowledgeBaseItems = [
        { question: 'What type of service do you provide?', answer: data.service_type },
        { question: 'What areas do you serve?', answer: data.service_areas },
        { question: 'Why should I work with you?', answer: data.why_work_with_you },
        { question: 'What is your website url?', answer: data.website_url || '' },
        // Auto-generated questions from organization data
        {
          question: 'What is the company/agent name?',
          answer: organization.org_name || 'Not provided',
        },
        {
          question: 'What is the office phone number?',
          answer: organization.org_phone || 'Not provided',
        },
        {
          question: 'What is the office email address?',
          answer: organization.email || 'Not provided',
        },
        {
          question: 'What is the office address?',
          answer: organization.client_address
            ? `${organization.client_address.street}, ${organization.client_address.city}, ${organization.client_address.state} ${organization.client_address.zip}`
            : 'Not provided',
        },

        ...(data.custom_questions || []),
      ];

      // Save knowledge base to KnowledgeBases collection
      const kbName = data.kb_name || `${organization.org_name} FAQ`;
      await addKnowledgeBase.mutateAsync({
        kb_name: kbName,
        knowledge_base: knowledgeBaseItems,
        organization_id: organization.id,
        type: 'general',
      });

      // Prepare the update data
      const updateData: any = {
        remi_intro_name: data.remi_intro_name,
        assistant_name: data.assistant_name,
        assistant_name_pronunciation: data.assistant_name_pronunciation,
        call_settings: {
          voice: data.voice || '',
          background_track: data.background_track || '',
          record: true,
        },
        onboardingSetup: {
          ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
          stage3: {
            completed: true,
            createdAt: new Date().toISOString(),
          },
        },
      };

      // If Twilio data is provided, save it as well
      if (data.twilioData) {
        saveStage4Data(data.twilioData);

        // Save EIN/CBN to Secret Manager (if provided)
        if (data.twilioData.ein_cbn) {
          await saveBusinessTaxSecrets(organization.id, {
            ein_cbn: data.twilioData.ein_cbn,
          });
        }

        // Save Twilio data (without sensitive EIN/CBN)
        const { ein_cbn, ...twilioDataWithoutEin } = data.twilioData;
        updateData.onboardingData = {
          ...(useOrganizationStore.getState().organization?.onboardingData ?? {}),
          twilio: {
            ...twilioDataWithoutEin,
            has_ein: Boolean(ein_cbn), // Flag to indicate EIN is stored in Secret Manager
            status: 'pending_verification',
          },
        };
      }

      // Save voice settings and Twilio data to Organization document
      await updateSettings({
        id: organization.id,
        data: updateData,
      });

      // Webhook is sent from Stage3RemiSetup when Twilio step completes
      console.log('Stage 3 saved to Firebase:', data);

      // Clear both React Query and Zustand caches
      await clearOrganizationCache(queryClient, fetchOrganization);

      // Fire-and-forget: Start creating flows in background
      // This runs while user continues to Stage 4 (IDX setup)
      // Safety flag prevents duplicate creation if triggered again
      createAllFlows()
        .then((result) => {
          if (result.success && result.flowsCreated.length > 0) {
            console.log('Background flow creation completed:', result.flowsCreated);
          }
        })
        .catch((err) => {
          console.error('Background flow creation failed:', err);
        });

      showToast({
        message: 'Settings saved successfully',
        variant: 'success',
      });

      // Scroll to top (Twilio banner and stage are now updated with fresh data)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting stage 3:', error);
      showToast({
        message: 'Failed to save assistant settings',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStage4Submit = async (data: IDXSetupData) => {
    if (!organization?.id) {
      showToast({
        message: 'No organization found',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const siteData = data.site_data as any;

      // Save IDX domain registrar secrets to Secret Manager (if provided)
      // This is when user needs help with domain setup and hasn't already shared access
      if (siteData?.needs_domain_help && !siteData?.domain_access_shared) {
        if (siteData.registrar_username || siteData.registrar_password) {
          await saveIDXDomainSecrets(organization.id, {
            domain_registrar: siteData.domain_registrar,
            registrar_username: siteData.registrar_username,
            registrar_password: siteData.registrar_password,
            registrar_email: siteData.registrar_email,
            registrar_phone: siteData.registrar_phone,
          });
        }
      }

      // IDX site creation and file uploads are now handled in Stage4IDXSetupWrapper
      // Here we just save the MLS data and mark stage 4 as complete

      // Build IDX data object for onboardingData
      // Site data already saved with logo/favicon URLs in Stage4IDXSetupWrapper
      const idxData: any = {
        mls_entries: data.mls_data?.mls_entries || [],
        status: 'pending_approval',
      };

      // Use site_data from wrapper (already has logo_url, favicon_url)
      if (data.site_data) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { logo_file, favicon_file, ...siteDataWithoutFiles } = siteData;
        idxData.site_data = siteDataWithoutFiles;
      }

      // Build update data object
      const updateData: any = {
        onboardingData: {
          ...(useOrganizationStore.getState().organization?.onboardingData ?? {}),
          idx: idxData,
        },
        onboardingSetup: {
          ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
          stage4: {
            completed: true,
            createdAt: new Date().toISOString(),
          },
        },
      };

      // Save onboarding tracking data
      await updateSettings({
        id: organization.id,
        data: updateData,
      });

      console.log('Stage 4 saved to Firebase:', data);

      // Clear both React Query and Zustand caches
      await clearOrganizationCache(queryClient, fetchOrganization);

      showToast({
        message: 'MLS setup saved successfully',
        variant: 'success',
      });

      // Scroll to top (stage is now updated with fresh data)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting stage 4:', error);

      showToast({
        message: error instanceof Error ? error.message : 'Failed to save MLS setup',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStage5Submit = async (data: CRMSetupData) => {
    if (!organization?.id) {
      showToast({
        message: 'No organization found',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Save CRM credentials to Secret Manager (if any non-FUB CRMs with credentials)
      await saveCRMCredentials(organization.id, data.crm_entries);

      // Build CRM entries array for onboardingData (without sensitive fields)
      // Sensitive data is now stored in Secret Manager, not Firestore
      const crmEntries = data.crm_entries.map((entry) => ({
        crm_type: entry.crm_type,
        crm_other_name: entry.crm_other_name,
        ai_disclaimer_acknowledged: Boolean(entry.ai_disclaimer_acknowledged),
        // Store non-sensitive info in Firestore
        ...(entry.crm_type !== 'fub' && {
          login_url: entry.login_url,
          two_fa_contact: entry.two_fa_contact,
          // Note: Passwords and API keys are now stored in Secret Manager
          has_credentials: Boolean(
            entry.super_admin_api_key || entry.super_admin_username || entry.super_admin_password
          ),
        }),
        // Brivity-specific non-sensitive info
        ...(entry.crm_type === 'brivity' && {
          brivity_website_login_url: entry.brivity_website_login_url,
          has_brivity_credentials: Boolean(entry.brivity_username || entry.brivity_password),
        }),
        status: entry.crm_type === 'fub' ? 'configured' : 'pending_setup',
      }));

      // Save CRM data to Organization document with onboarding tracking
      await updateSettings({
        id: organization.id,
        data: {
          onboardingData: {
            ...(useOrganizationStore.getState().organization?.onboardingData ?? {}),
            crm: {
              crm_entries: crmEntries,
            },
            // Persist consent acknowledgment (same field IntegrationsModal reads)
            ...(data.crm_entries.some((e) => e.ai_disclaimer_acknowledged) && {
              crm_consent_acknowledged: true,
              crm_consent_acknowledged_at: new Date().toISOString(),
            }),
          },
          onboardingSetup: {
            ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
            stage5: {
              completed: true,
              createdAt: new Date().toISOString(),
            },
          },
        } as any,
      });

      console.log('Stage 5 saved to Firebase (secrets saved separately):', data);

      // Clear both React Query and Zustand caches
      await clearOrganizationCache(queryClient, fetchOrganization);

      showToast({
        message: 'CRM setup saved successfully',
        variant: 'success',
      });

      // Scroll to top (stage is now updated with fresh data)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting stage 5:', error);
      showToast({
        message: 'Failed to save CRM setup',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCurrentStage = () => {
    // Show loading while organization is being fetched
    if (!organization) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    switch (currentStage) {
      case 0:
        return <Stage0Welcome userName={user?.displayName || undefined} onStart={() => setUserHasStarted(true)} />;
      case 1:
        return (
          <Stage1OrganizationSetup
            onNext={handleStage1Submit}
            initialData={
              onboardingData.stage1 || {
                org_name: organization?.org_name || '',
                org_phone: organization?.org_phone || '',
                email: organization?.email || '',
                notification_email: (organization as any)?.notification_email || '',
                timezone: organization?.timezone || '',
                client_address: {
                  street: organization?.client_address?.street || '',
                  city: organization?.client_address?.city || '',
                  state: organization?.client_address?.state || '',
                  zip: organization?.client_address?.zip || '',
                  country: (organization?.client_address as any)?.country || ('US' as const),
                },
              }
            }
            isLoading={isSubmitting}
          />
        );
      case 2:
        return (
          <Stage2WebsiteBranding
            onNext={handleStage2Submit}
            initialData={organization.onboardingData?.website || onboardingData.stage2}
            isLoading={isSubmitting}
            existingDomain={(organization?.business_website as string) || ''}
          />
        );
      case 3: {
        // Get website URL from Stage 2 data as default for Remi setup
        const websiteUrlDefault =
          organization.onboardingData?.website?.domain_name ||
          (organization as any)?.business_website ||
          '';

        return (
          <Stage3RemiSetup
            onNext={handleStage3Submit}
            initialData={{
              remi_intro_name: (organization?.remi_intro_name as string) || '',
              assistant_name: (organization?.assistant_name as string) || 'Remi',
              assistant_name_pronunciation:
                (organization?.assistant_name_pronunciation as string) || '',
              voice: (organization?.call_settings?.voice as string) || '',
              background_track: (organization?.call_settings?.background_track as string) || '',
              kb_name:
                ((organization as any)?.onboardingData?.remi?.kb_name as string) ||
                `${organization?.org_name || 'Organization'} FAQ`,
              service_type:
                ((organization as any)?.onboardingData?.remi?.service_type as string) || '',
              service_areas:
                ((organization as any)?.onboardingData?.remi?.service_areas as string) || '',
              why_work_with_you:
                ((organization as any)?.onboardingData?.remi?.why_work_with_you as string) || '',
              website_url:
                ((organization as any)?.onboardingData?.remi?.website_url as string) ||
                websiteUrlDefault,
              custom_questions:
                ((organization as any)?.onboardingData?.remi?.custom_questions as Array<{
                  question: string;
                  answer: string;
                }>) || [],
            }}
            initialTwilioData={organization.onboardingData?.twilio || onboardingData.stage4}
            isLoading={isSubmitting}
            organizationName={organization?.org_name || ''}
            officeAddress={
              onboardingData.stage1?.client_address || {
                street: organization?.client_address?.street || '',
                city: organization?.client_address?.city || '',
                state: organization?.client_address?.state || '',
                zip: organization?.client_address?.zip || '',
                country: (organization?.client_address?.country as 'US' | 'Canada') || 'US',
              }
            }
          />
        );
      }
      case 4:
        return (
          <Stage4IDXSetupWrapper
            onNext={handleStage4Submit}
            initialData={(organization as any)?.onboardingData?.idx}
            isLoading={isSubmitting}
            userEmail={user?.email || ''}
            userName={user?.displayName || ''}
          />
        );
      case 5:
        return (
          <Stage5CRMSetup
            onNext={handleStage5Submit}
            initialData={(organization as any)?.onboardingData?.crm || {}}
            isLoading={isSubmitting}
            organization={organization}
            onFubSuccess={async () => {
              // Refresh data after FUB setup
              await clearOrganizationCache(queryClient, fetchOrganization);
            }}
          />
        );
      case 6:
        return (
          <LastStageGuide
            organizationName={organization?.org_name || 'your organization'}
            userName={user?.displayName || undefined}
            onComplete={async () => {
              // Mark stage 6 (review) as complete in Firebase
              if (organization?.id) {
                try {
                  await updateSettings({
                    id: organization.id,
                    data: {
                      onboardingSetup: {
                        ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
                        stage6: {
                          completed: true,
                          createdAt: new Date().toISOString(),
                        },
                        revive_tour_completed: false,
                      },
                      isOnboardingComplete: true,
                    } as any,
                  });
                  // Clear caches so app knows onboarding is complete
                  await clearOrganizationCache(queryClient, fetchOrganization);
                } catch (error) {
                  console.error('Error completing onboarding:', error);
                }
              }
            }}
          />
        );
      default:
        return null;
    }
  };

  // Show pre-welcome full screen image first (only for new users at Stage 0)
  if (showPreWelcome && currentStage === 0) {
    return <StagePreWelcome onContinue={() => setShowPreWelcome(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-yellow-50 dark:from-gray-950 dark:to-gray-900">
      {/* Status Banners - sticky container for all banners */}
      <div className="sticky top-0 z-40">
        <TwilioStatusBanner organization={organization} />
        <MLSStatusBanner organization={organization} />
        <WebsiteStatusBanner organization={organization} />
        <IntegrationsStatusBanner organization={organization} />
      </div>

      {currentStage > 0 && (
        <>
          {/* Header */}
          <div className="bg-white dark:bg-gray-900 py-8 ">
            <Container>
              <div className="flex flex-col items-center gap-3">
                {/*<img src="/speculo.ai-black.png" alt="Speculo.ai" className="h-12 w-auto" /> */}

                <div className="flex flex-wrap items-center justify-center gap-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-black dark:text-white">Welcome to </h1>
                  <img
                    src="/speculo.ai-black-with-text.png"
                    alt="Speculo.ai"
                    className="h-7 sm:h-8 md:h-10 w-auto dark:invert"
                  />
                </div>
                <p className="text-center text-gray-600 dark:text-gray-400 max-w-2xl">
                  Let's get your account set up in a few simple steps
                </p>
              </div>
            </Container>
          </div>

          {/* Progress Tracker */}
          <OnboardingProgress
            currentStage={currentStage}
            totalStages={ONBOARDING_STAGES.length}
            stages={ONBOARDING_STAGES}
            organization={organization}
          />
        </>
      )}

      {/* Current Stage Content */}
      <div>{renderCurrentStage()}</div>

      {/* Floating Help Button */}
      <button
        onClick={() => setIsScheduleCallModalOpen(true)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 p-3 sm:p-4 bg-yellow-400 hover:bg-yellow-500 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group"
        title="Have any questions? Schedule a call"
      >
        <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
      </button>

      {/* Schedule Call Modal */}
      <ScheduleCallModal
        isOpen={isScheduleCallModalOpen}
        onClose={() => setIsScheduleCallModalOpen(false)}
        emailOnly
      />
    </div>
  );
};
